import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Shrimp, Users, ClipboardText, Scales, ArrowsClockwise, PencilSimple, FloppyDisk, X, MapPin, Phone, EnvelopeSimple, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ProfilPage() {
  const { user } = useAuth();
  const [profil, setProfil] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/profil").then(({ data }) => {
      setProfil(data);
      setForm({
        nama: data.nama || "", nama_lengkap: data.nama_lengkap || "",
        lokasi: data.lokasi || "", alamat: data.alamat || "",
        telepon: data.telepon || "", email: data.email || "",
        deskripsi: data.deskripsi || "", tahun_berdiri: data.tahun_berdiri || "",
        jumlah_anggota: data.jumlah_anggota || 0,
        total_luas_tambak: data.total_luas_tambak || 0,
        visi: data.visi || "", misi: data.misi || "",
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    try {
      await api.put("/profil", form);
      toast.success("Profil berhasil diperbarui");
      setEditing(false);
      const { data } = await api.get("/profil");
      setProfil(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
  };

  const canEdit = ["admin", "manager"].includes(user?.role);
  const stats = profil?.stats || {};

  if (loading) return <div className="flex items-center justify-center py-20 text-slate-400 text-sm">Memuat profil...</div>;

  return (
    <div data-testid="profil-page" className="space-y-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#1A1A1A] to-[#2D2D2D] p-6 sm:p-8">
        <div className="absolute top-0 right-0 w-40 h-40 bg-[#B8860B]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#008080]/10 rounded-full translate-y-1/2 -translate-x-1/2" />
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <img src="/logo-p3uw.png" alt="P3UW" className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover shadow-2xl border-2 border-[#B8860B]/30" />
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" style={{ fontFamily: "Work Sans" }}>
              {profil?.nama_lengkap || profil?.nama || "P3UW"}
            </h1>
            <p className="text-sm text-[#B8860B] font-medium mt-1">{profil?.nama || "P3UW"} &mdash; {profil?.lokasi || "Lampung"}</p>
            <p className="text-sm text-slate-400 mt-2 max-w-xl">{profil?.deskripsi}</p>
          </div>
          {canEdit && !editing && (
            <Button onClick={() => setEditing(true)} data-testid="edit-profil-btn" variant="outline" className="border-[#B8860B]/50 text-[#B8860B] hover:bg-[#B8860B]/10 shrink-0">
              <PencilSimple size={16} className="mr-1.5" /> Edit Profil
            </Button>
          )}
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: "Tambak", value: stats.total_tambak, icon: Shrimp, color: "text-[#B8860B]" },
          { label: "Siklus", value: stats.total_siklus, icon: ArrowsClockwise, color: "text-[#008080]" },
          { label: "Pengguna", value: stats.total_pengguna, icon: Users, color: "text-blue-600" },
          { label: "Laporan", value: stats.total_laporan, icon: ClipboardText, color: "text-purple-600" },
          { label: "Panen", value: stats.total_panen, icon: Scales, color: "text-amber-600" },
        ].map((s, i) => (
          <Card key={i} className="bg-white border border-slate-200">
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon size={22} weight="duotone" className={s.color} />
              <div>
                <p className="text-xl font-bold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>{s.value ?? 0}</p>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Info + Edit */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>Informasi Koperasi</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {editing ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Nama Singkat</Label><Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Lokasi</Label><Input value={form.lokasi} onChange={(e) => setForm({ ...form, lokasi: e.target.value })} /></div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Nama Lengkap</Label><Input value={form.nama_lengkap} onChange={(e) => setForm({ ...form, nama_lengkap: e.target.value })} /></div>
                <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Deskripsi</Label><Textarea value={form.deskripsi} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} rows={3} /></div>
                <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Alamat</Label><Input value={form.alamat} onChange={(e) => setForm({ ...form, alamat: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Telepon</Label><Input value={form.telepon} onChange={(e) => setForm({ ...form, telepon: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Tahun Berdiri</Label><Input value={form.tahun_berdiri} onChange={(e) => setForm({ ...form, tahun_berdiri: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Jml Anggota</Label><Input type="number" value={form.jumlah_anggota} onChange={(e) => setForm({ ...form, jumlah_anggota: parseInt(e.target.value) || 0 })} /></div>
                  <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Luas Tambak (ha)</Label><Input type="number" step="0.1" value={form.total_luas_tambak} onChange={(e) => setForm({ ...form, total_luas_tambak: parseFloat(e.target.value) || 0 })} /></div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={handleSave} data-testid="save-profil-btn" className="bg-[#B8860B] hover:bg-[#9A7209] text-white"><FloppyDisk size={16} className="mr-1.5" /> Simpan</Button>
                  <Button variant="outline" onClick={() => setEditing(false)}><X size={16} className="mr-1.5" /> Batal</Button>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                {[
                  { icon: MapPin, label: "Lokasi", value: profil?.alamat || profil?.lokasi || "-" },
                  { icon: Phone, label: "Telepon", value: profil?.telepon || "-" },
                  { icon: EnvelopeSimple, label: "Email", value: profil?.email || "-" },
                  { icon: CalendarBlank, label: "Tahun Berdiri", value: profil?.tahun_berdiri || "-" },
                  { icon: Users, label: "Jumlah Anggota", value: profil?.jumlah_anggota || "-" },
                  { icon: Shrimp, label: "Total Luas Tambak", value: profil?.total_luas_tambak ? `${profil.total_luas_tambak} ha` : "-" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0">
                    <item.icon size={18} className="text-slate-400 shrink-0" />
                    <span className="text-xs uppercase tracking-wider font-semibold text-slate-500 w-28 shrink-0">{item.label}</span>
                    <span className="text-sm text-[#1A1A1A]">{item.value}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>Visi & Misi</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {editing ? (
              <>
                <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Visi</Label><Textarea value={form.visi} onChange={(e) => setForm({ ...form, visi: e.target.value })} rows={4} /></div>
                <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Misi</Label><Textarea value={form.misi} onChange={(e) => setForm({ ...form, misi: e.target.value })} rows={6} /></div>
              </>
            ) : (
              <>
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-[#B8860B] mb-2">Visi</p>
                  <p className="text-sm text-slate-700 leading-relaxed">{profil?.visi || "Belum diisi"}</p>
                </div>
                <div className="border-t border-slate-100 pt-4">
                  <p className="text-xs uppercase tracking-wider font-semibold text-[#008080] mb-2">Misi</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{profil?.misi || "Belum diisi"}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
